import { CloseOutlined, LeftOutlined, LoadingOutlined, RightOutlined } from '@ant-design/icons';
import { MapSourceTile } from '@omniafishing/core';
import { Slider, SliderSingleProps, Tooltip } from 'antd';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { isDone, isError, isPending, LoadingState } from '../../../../constants/loading_state';
import { tileMeetsMinimumCoverage } from '../../../../routes/map_page/lib/map_page_helpers';
import { OmniaButton } from '../../../omnia_button/omnia_button';
import styles from './slider_modal.less';

interface WaterTempModalProps {
  onChange: (index: number) => void;
  onClose: () => void;
  displayedDate: any;
  sliderIdx: number;
  tiles: MapSourceTile[];
  open: boolean;
  loadingState: LoadingState;
}

export const getIsNextButtonActive = (tiles: MapSourceTile[], sliderIdx: number) => {
  const nextTiles = tiles.slice(sliderIdx + 1);
  return !nextTiles.some(tileMeetsMinimumCoverage);
};

export const getIsPreviousButtonActive = (tiles: MapSourceTile[], sliderIdx: number) => {
  const previousTiles = tiles.slice(0, sliderIdx);
  return !previousTiles.some(tileMeetsMinimumCoverage);
};

export const WaterTempSliderModal = (props: WaterTempModalProps) => {
  const { loadingState, onChange, onClose, open, displayedDate, sliderIdx, tiles } = props;

  const isNextButtonActive = useMemo(() => {
    return getIsNextButtonActive(tiles, sliderIdx);
  }, [tiles, sliderIdx]);

  const isPreviousButtonActive = useMemo(() => {
    return getIsPreviousButtonActive(tiles, sliderIdx);
  }, [tiles, sliderIdx]);

  if (!open) {
    return null;
  }

  const mostRecentSelectableDate = tiles
    .filter((t) => tileMeetsMinimumCoverage(t))
    .slice(-1)[0]?.date;
  const mostRecentSelectableDateIsOld = dayjs(mostRecentSelectableDate).isBefore(
    dayjs().subtract(2, 'week')
  );

  const formatter: NonNullable<SliderSingleProps['tooltip']>['formatter'] = () =>
    displayedDate?.format('MMM D, YYYY');

  const hasMultipleValidDates = tiles.filter(tileMeetsMinimumCoverage).length > 1;

  return (
    <div className={styles.modal}>
      <header>
        <span className={styles.heading}>Water Temperature Historical Data</span>
        <span className={styles.close} onClick={onClose}>
          <span className={styles.closeIcon}>
            <CloseOutlined />
          </span>
        </span>
      </header>
      {isPending(loadingState) && (
        <div className={styles.loading}>
          <LoadingOutlined />
        </div>
      )}
      {isDone(loadingState) && (
        <>
          {tiles.length > 0 && sliderIdx > -1 ? (
            <>
              <div className={styles.sliderContainer}>
                {hasMultipleValidDates && (
                  <OmniaButton
                    kind="tertiary"
                    shape="circle"
                    size="sm"
                    onPress={() => {
                      const previousTiles = tiles.slice(0, sliderIdx);
                      for (let i = previousTiles.length - 1; i >= 0; i--) {
                        if (tileMeetsMinimumCoverage(previousTiles[i])) {
                          onChange(i);
                          return;
                        }
                      }
                    }}
                    isDisabled={isPreviousButtonActive}
                    className={styles.circleButton}
                  >
                    <LeftOutlined />
                  </OmniaButton>
                )}
                <Slider
                  marks={tiles.reduce((acc, tile, i) => {
                    if (tileMeetsMinimumCoverage(tile)) {
                      acc[i] = {
                        label: <span className={styles.sliderLabel}>{tile.date}</span>,
                      };
                    }
                    return acc;
                  }, {} as Record<number, any>)}
                  tooltip={{
                    formatter,
                    open: true,
                    getPopupContainer(triggerNode) {
                      return triggerNode.parentElement as HTMLElement;
                    },
                  }}
                  step={null}
                  min={0}
                  max={tiles.length - 1}
                  value={sliderIdx}
                  onChange={onChange}
                  className={styles.slider}
                  trackStyle={{
                    backgroundColor: '#3F3F46',
                    height: 8,
                    borderRadius: 36,
                  }}
                  railStyle={{
                    backgroundColor: '#A1A1AA80',
                    height: 8,
                    borderRadius: 36,
                  }}
                  handleStyle={{
                    borderColor: '#3F3F46',
                    borderWidth: 6,
                    marginTop: -4,
                    width: 16,
                    height: 16,
                  }}
                />
                {hasMultipleValidDates && (
                  <OmniaButton
                    kind="tertiary"
                    shape="circle"
                    size="sm"
                    isDisabled={isNextButtonActive}
                    onPress={() => {
                      const nextTiles = tiles.slice(sliderIdx + 1);
                      const nextTileIdx = nextTiles.findIndex(tileMeetsMinimumCoverage);
                      if (nextTileIdx > -1) {
                        onChange(sliderIdx + 1 + nextTileIdx);
                      }
                    }}
                    className={styles.circleButton}
                  >
                    <RightOutlined />
                  </OmniaButton>
                )}
              </div>
              <div className={styles.sliderDates}>
                <span>
                  {mostRecentSelectableDateIsOld && (
                    <Tooltip title="Our satellite-based water temperature data may fall behind due to natural factors like cloud cover and satellite schedules. We will always show you the most up-to-date information available.">
                      <span className={styles.oldData}>Why isn't this more recent?</span>
                    </Tooltip>
                  )}
                </span>
              </div>
            </>
          ) : (
            <span className={styles.noCoverage}>No Coverage In This Area</span>
          )}
        </>
      )}
      {isError(loadingState) && (
        <>
          <span className={styles.error}>
            There was an error loading water temperature data. <br />
            Please try again in a moment.
          </span>
        </>
      )}
    </div>
  );
};
